import { Table, Tag } from 'antd';
import React, { useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DAYTIME_FORMAT } from '../../constants';
import useColumnConfig from '../../hooks/useColumnConfig';
import ColumnConfigDrawer from '../ColumnConfigDrawer';
import PartnerLogo from '../PartnerLogo';

const FIXED_KEYS = ['name', 'action'];

export default function PartnersTable({
  withAction,
  dataSource,
  selectedRowKeys,
  onSelectChange,
  onRowClick,
  configRef,
  onPageSizeChange,
  ...props
}) {
  const { t } = useTranslation('partners');
  const { t: tc } = useTranslation('common');
  const allColumns = useMemo(() => {
    const cols = [
      {
        title: tc('name'),
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        className: 'border-right',
        render: (value, record) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <PartnerLogo name={record?.name} logoKey={record?.logoFile?.key} />
            <span>{value || '—'}</span>
          </span>
        ),
      },
      {
        title: tc('code'),
        dataIndex: 'code',
        key: 'code',
        width: 150,
      },
      {
        title: t('email'),
        dataIndex: 'contactEmail',
        key: 'contactEmail',
        width: 220,
        render: (value) => value || '—',
      },
      {
        title: t('phone'),
        dataIndex: 'contactPhone',
        key: 'contactPhone',
        width: 160,
        render: (value) => value || '—',
      },
      {
        title: t('orders'),
        key: 'ordersCount',
        width: 100,
        align: 'center',
        render: (record) => {
          const count = record._count?.orders || 0;
          return count > 0 ? <Tag>{count}</Tag> : '—';
        },
      },
      {
        title: t('itemTypesCount'),
        key: 'itemTypesCount',
        width: 110,
        align: 'center',
        render: (record) => {
          const count = record._count?.partnerItemTypes || 0;
          return count > 0 ? <Tag color="blue">{count}</Tag> : '—';
        },
      },
      {
        title: tc('created'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
    ];

    if (withAction) {
      cols.push({
        key: 'action',
        width: 120,
        className: 'border-left',
        fixed: 'right',
        render(record) {
          return withAction(record);
        },
      });
    }

    return cols;
  }, [withAction]);

  const columnConfig = useColumnConfig('partners-columns', allColumns, FIXED_KEYS);

  // Expose openDrawer to parent via ref
  if (configRef) {
    configRef.current = { openDrawer: columnConfig.openDrawer, pageSize: columnConfig.pageSize };
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="table-job-orders table-common">
      <Table
        columns={columnConfig.visibleColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-dark' : 'table-row-light')}
        onRow={(record) => ({
          onClick: () => onRowClick && onRowClick(record),
          style: onRowClick ? { cursor: 'pointer' } : undefined,
        })}
        {...props}
      />
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={(size) => {
          columnConfig.setPageSize(size);
          // Server-paginated page — tell it to refetch with the new take.
          onPageSizeChange?.(size);
        }}
      />
    </div>
  );
}
